import Actions from '../actions'
import { getUrlParams } from './useParamUtil'
import validateZipcode from './validateZipcode'
import { isEmpty } from 'ramda'
import dayjs from 'dayjs'
import { ProviderPCPTypes, ProvidersTiersMap } from 'consts'
import {
  DEFAULT_STATE,
  DEFAULT_SUPPORTED_STATES,
  INDIVIDUAL_PROVIDER_TYPE
} from 'consts/ProviderConsts'

/**
 * Returns the display name for a provider or facility with highlighted name and practice category code if available
 * @param {Object} item - Provider or facility object
 * @param {string} item.highlighted_name - Highlighted name if available
 * @param {string} item.name - Regular name
 * @param {string} [item.prac_category_code] - Practice category code if available
 * @returns {string} - Display name with highlighting and category code
 */
export const getProviderDisplayName = item => {
  const initialName = item.highlighted_name || item.name
  return item.prac_category_code
    ? `${initialName}, ${item.prac_category_code}`
    : initialName
}

const extractParams = (source = {}, fields) => {
  return Object.entries(source)
    .filter(([key, value]) => fields.includes(key) && value != null)
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: key === 'distanceRadius' ? value.toString() : value
      }),
      {}
    )
}

export const getLocationParams = (location = {}) => {
  return extractParams(location, ['city', 'state', 'zipcode'])
}

export const getPcpParams = (patientsPcpProviders = [], pcpType) => {
  const pcpIds = patientsPcpProviders
    .filter(pcp => pcp.type === pcpType)
    .map(pcp => pcp.pcpId)
    .join(',')

  return pcpIds || null
}

export const getFilterParams = (filters = {}) => {
  // Only use explicitly set radius from filters, otherwise let backend use default
  return extractParams(filters, [
    'language',
    'gender',
    'tier',
    'providerType',
    'acceptingNewPatients',
    'distanceRadius',
    'hospitalAffiliations',
    'medicalGroupAffiliations',
    'year'
  ])
}

/**
 * Counts the number of active filters in a filters object
 * @param {Object} filters - The filters object to count
 * @param {Object} options - Options for counting
 * @param {Array} options.filterKeys - Array of filter keys to check (defaults to standard filter keys)
 * @param {Array} options.excludeKeys - Array of keys to exclude from counting
 * @returns {number} - Number of active filters
 */
export const countActiveFilters = (filters = {}, options = {}) => {
  const {
    filterKeys = [
      'language',
      'gender',
      'tier',
      'providerType',
      'acceptingNewPatients',
      'distanceRadius',
      'hospitalAffiliations',
      'medicalGroupAffiliations'
    ],
    excludeKeys = []
  } = options

  return filterKeys
    .filter(key => !excludeKeys.includes(key))
    .reduce((count, key) => {
      const value = filters[key]

      // Check if filter is active (not null, undefined, or empty)
      if (value != null && value !== undefined && !isEmpty(value)) {
        return count + 1
      }

      return count
    }, 0)
}

export const isAssignedPcp = provider => {
  return Boolean(
    provider?.is_assigned_th_pcp || provider?.is_assigned_physical_pcp
  )
}

export function updateProvidersSearchFromUrl(
  dispatch,
  userDetails,
  supportedStates = DEFAULT_SUPPORTED_STATES,
  defaultState = DEFAULT_STATE
) {
  const queryParams = getUrlParams()

  const isChangePcpFlow = queryParams?.changePcp === 'true'
  const changePcpType = queryParams?.changePcpType

  let state = null
  if (userDetails) {
    state = userDetails?.address?.find(ad => !!ad.state)?.state
  } else {
    state = queryParams?.state?.toUpperCase()
  }
  if (!supportedStates.includes(state)) state = defaultState
  let city = queryParams?.city || null
  let zipcode = queryParams?.zipcode || null

  if (zipcode) {
    const stateCode = validateZipcode(queryParams.zipcode)
    if (supportedStates.includes(stateCode)) {
      state = stateCode
    } else {
      zipcode = null
    }
  }

  dispatch(
    Actions.createAction(Actions.SEARCH_BY_LOCATION_UPDATED, {
      city,
      state,
      zipcode
    })
  )

  if (isChangePcpFlow) {
    queryParams.query = 'PCP'
    queryParams.tier =
      changePcpType === ProviderPCPTypes.TELEHEALTH
        ? ProvidersTiersMap.antidote
        : ProvidersTiersMap.tier1
    queryParams.providerType = INDIVIDUAL_PROVIDER_TYPE
  }

  // Update search query in Redux from URL params
  dispatch(
    Actions.createAction(
      Actions.PROVIDERS_SEARCH_QUERY_UPDATED,
      queryParams.query
    )
  )

  // Update filters in Redux from URL params
  dispatch(
    Actions.createAction(Actions.SEARCH_PROVIDER_FILTERS_UPDATED, {
      language: queryParams?.language || null,
      gender: queryParams?.gender || null,
      tier: queryParams?.tier || null,
      providerType: queryParams?.providerType || null,
      acceptingNewPatients:
        queryParams?.acceptingNewPatients === 'true' || null,
      distanceRadius: queryParams?.distanceRadius || 50,
      hospitalAffiliations: queryParams?.hospitalAffiliations || null,
      medicalGroupAffiliations: queryParams?.medicalGroupAffiliations || null,
      year: queryParams?.year || '2026'
    })
  )

  return queryParams
}

export const getYearFromDate = date => {
  return dayjs(date).year()
}

// Returns an array of valid contract years based on start and end dates.
export const getContractPeriod = (startDate, endDate) => {
  // Accept any year 2025 or after as valid
  const BASE_YEAR = 2025
  const isValidContractYear = year => year >= BASE_YEAR
  const startYear = dayjs(startDate).year()
  const endYear = dayjs(endDate).year()

  if (!startDate && !endDate) {
    // Return an array from the base contract year up to and including the current year.
    return [BASE_YEAR, BASE_YEAR + 1]
  }
  if (!startDate && endDate && isValidContractYear(endYear)) {
    if (endYear === BASE_YEAR) {
      return [BASE_YEAR]
    } else {
      return [endYear - 1, endYear]
    }
  }

  if (!endDate && startDate && isValidContractYear(startYear)) {
    if (startYear === BASE_YEAR) {
      return [startYear, startYear + 1]
    } else {
      return [startYear]
    }
  }

  //if we have both then return only the ones after the base year.
  const filteredYears = new Set()
  if (isValidContractYear(startYear)) {
    filteredYears.add(startYear)
  }
  if (isValidContractYear(endYear)) {
    filteredYears.add(endYear)
  }
  return Array.from(filteredYears)
}

export const isIndividualProvider = provider => {
  return provider?.provider_type === INDIVIDUAL_PROVIDER_TYPE
}

export const isAntidoteProvider = provider => {
  return provider?.is_antidote || provider?.tier === ProvidersTiersMap.antidote
}

//temporary speciality for antidote providers until full data is available
export const getProviderSpecialities = provider => {
  if (!provider?.specialities?.length) {
    return ''
  }

  return provider.specialities.map(speciality => speciality?.name).join(', ')
}
